import type { LucideIcon } from 'lucide-react'
import {
  BarChart3,
  Bot,
  Cable,
  Headset,
  Inbox,
  LayoutGrid,
  MessageCircleQuestion,
  MessagesSquare,
  Radio,
  ScrollText,
  Settings,
  Users,
  Wallet,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useTriageOverview } from '@/features/triage/queries'

export type AppRoutePath =
  | '/app/$tenantSlug/dashboard'
  | '/app/$tenantSlug/desk'
  | '/app/$tenantSlug/pendencias'
  | '/app/$tenantSlug/agents'
  | '/app/$tenantSlug/knowledge'
  | '/app/$tenantSlug/channels'
  | '/app/$tenantSlug/webchat'
  | '/app/$tenantSlug/integrations'
  | '/app/$tenantSlug/analytics'
  | '/app/$tenantSlug/audit'
  | '/app/$tenantSlug/team'
  | '/app/$tenantSlug/billing'
  | '/app/$tenantSlug/settings'

export interface NavSectionItem {
  readonly key: string
  readonly to: AppRoutePath
  readonly icon: LucideIcon
  readonly label: string
  /** Contador ao lado do item (ex.: conversas esperando você). Omitido/0 = sem selo. */
  readonly badge?: number
}

export interface NavSections {
  readonly primary: readonly NavSectionItem[]
  readonly secondary: readonly NavSectionItem[]
}

/** Navegacao primaria/secundaria da shell (docs/design/02-INFORMATION-ARCHITECTURE.md secao 6). */
export function useNavSections(tenantId?: string): NavSections {
  const { t } = useTranslation()
  // Selo de Pendências: quantas conversas esperam você (polling do mesmo query da tela).
  const triage = useTriageOverview(tenantId)
  const awaitingYou = triage.data?.counts.awaitingYou ?? 0

  const primary: readonly NavSectionItem[] = [
    {
      key: 'dashboard',
      to: '/app/$tenantSlug/dashboard',
      icon: LayoutGrid,
      label: t('shell.nav.dashboard'),
    },
    { key: 'desk', to: '/app/$tenantSlug/desk', icon: Headset, label: t('shell.nav.desk') },
    {
      key: 'pendencias',
      to: '/app/$tenantSlug/pendencias',
      icon: Inbox,
      label: t('shell.nav.pendencias'),
      badge: awaitingYou,
    },
    { key: 'agents', to: '/app/$tenantSlug/agents', icon: Bot, label: t('shell.nav.agents') },
    {
      key: 'knowledge',
      to: '/app/$tenantSlug/knowledge',
      icon: MessageCircleQuestion,
      label: t('shell.nav.knowledge'),
    },
    {
      key: 'channels',
      to: '/app/$tenantSlug/channels',
      icon: Radio,
      label: t('shell.nav.channels'),
    },
    {
      key: 'webchat',
      to: '/app/$tenantSlug/webchat',
      icon: MessagesSquare,
      label: t('shell.nav.webchat'),
    },
    {
      key: 'integrations',
      to: '/app/$tenantSlug/integrations',
      icon: Cable,
      label: t('shell.nav.integrations'),
    },
    {
      key: 'analytics',
      to: '/app/$tenantSlug/analytics',
      icon: BarChart3,
      label: t('shell.nav.analytics'),
    },
  ]

  const secondary: readonly NavSectionItem[] = [
    { key: 'audit', to: '/app/$tenantSlug/audit', icon: ScrollText, label: t('shell.nav.audit') },
    { key: 'team', to: '/app/$tenantSlug/team', icon: Users, label: t('shell.nav.team') },
    {
      key: 'billing',
      to: '/app/$tenantSlug/billing',
      icon: Wallet,
      label: t('shell.nav.billing'),
    },
    {
      key: 'settings',
      to: '/app/$tenantSlug/settings',
      icon: Settings,
      label: t('shell.nav.settings'),
    },
  ]

  return { primary, secondary }
}
